import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { IMCMapView } from '../../../classes/mcMapView';
import { Callout } from '../../../classes/models/callout.model';
import { util } from '../../../classes/util';
import { ESRI } from '../../../esri-loader-util';
import { InputConverter } from '../../../inputConverter';
import { LayerService } from '../../../services/layer.service';
import { BaseComponent } from '../../base.component';

let Graphic: __esri.GraphicConstructor;
let SketchViewModel: __esri.SketchViewModelConstructor;
let SimpleFillSymbol: __esri.SimpleFillSymbolConstructor;

const pixelsInAPoint = 1.3333;
const widthPaddingMultiplier = 2;
const sketchUpdateTimeout = 10;
const initilizeDebounceTime = 50;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-callout-ng',
    styleUrls: ['../../sharedStyles/base.scss', './callout.component.scss'],
    templateUrl: './callout.component.html'
})
export class CalloutComponent extends BaseComponent implements OnInit {
    private _active: boolean = false;
    @Input() set active(value: boolean) {
        this._active = value;
        value ? this.activate() : this.deactivate();
        this.activeChange.emit(value);
    }
    get active(): boolean { return this._active; }

    @Input() @InputConverter() showImageUpload: boolean = false;

    @Output() readonly activeChange: EventEmitter<boolean> = new EventEmitter();

    callouts: Callout[] = [];
    activeCallout: Callout;

    layerName: string = 'Callouts';
    boxAlignmentOptions = ['Top Left', 'Bottom Left', 'Top Right', 'Bottom Right'];

    mapView: IMCMapView;
    sketchViewModel: NonNullable<__esri.SketchViewModel>;

    outlineMin = 0;
    outlineMax = 10;
    textSizeMin = 8;
    textSizeMax = 128;

    prevMapPointX: number;
    loaded: boolean = false;

    mapClickListener;
    doAddCallout: boolean = false;

    selectedImage: File = null;
    cbImageSource = null;
    imageOn: boolean = false;

    constructor(
        _vcr: ViewContainerRef,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly layerService: LayerService
    ) {
        super(_vcr);
        this.registerEvent('activeChange', this.activeChange);
    }

    fillColorPicker: ViewContainerRef;
    @ViewChild('fillColorPicker') set fillColorPickerRef(el: ViewContainerRef) { this.fillColorPicker = el; }
    outlineColorPicker: ViewContainerRef;
    @ViewChild('outlineColorPicker') set outlineColorPickerRef(el: ViewContainerRef) { this.outlineColorPicker = el; }
    textColorPicker: ViewContainerRef;
    @ViewChild('textColorPicker') set textColorPickerRef(el: ViewContainerRef) { this.textColorPicker = el; }

    async ngOnInit() {
        try {
            this.mapView = await this.setupMapViewRef(this.mapView);
            this.mapView.watch('scale', this.onScaleChange.bind(this));
            [Graphic, SketchViewModel, SimpleFillSymbol] = await Promise.all([ESRI.Graphic, ESRI.SketchViewModel, ESRI.SimpleFill]);
        } catch (err) {
            this.readyProblem(err);
        } finally {
            this.isReady();
        }
    }

    private initSketch() {
        this.sketchViewModel = new SketchViewModel({
            layer: this.activeCallout.graphicsLayer,
            defaultUpdateOptions: {
                enableRotation: false,
                multipleSelectionEnabled: false,
                toggleToolOnClick: false
            },
            updateOnGraphicClick: false,
            view: this.mapView,
            polygonSymbol: (<__esri.SimpleFillSymbol>this.activeCallout.calloutGraphic.symbol).clone()
        });
        this.sketchViewModel.on('update', this.sketchUpdate.bind(this));
    }

    private teardownSketch() {
        if (this.sketchViewModel) {
            this.sketchViewModel.complete();
            this.sketchViewModel.destroy();
            this.sketchViewModel = null;
        }
    }

    private onScaleChange() {
        if (this.sketchViewModel) {
            setTimeout(() => this.resetSketch(false), sketchUpdateTimeout);
        }
    }

    /**
     * Make the tool active/shown.
     */
    private async activate() {
        if (!this.mapClickListener) {
            this.mapClickListener = this.mapView.on('click', (evt) => this.handleMapClick(evt));
        }
        this.setupHandleHiddenLayer();
        this.doAddCallout = true;

        this.callouts = Callout.fromLayers(this.layerService.mapLayers.graphics);
    }

    /**
     * Make the tool inactive/hidden.
     */
    private deactivate(): void {
        this.activeCallout = null;
        this.teardownHandleHiddenLayer();
        if (this.mapClickListener) {
            this.mapClickListener.remove();
            this.mapClickListener = null;
        }

        if (this.sketchViewModel) {
            this.teardownSketch();
        }
        this.loaded = false;
    }

    private async handleMapClick(evt: __esri.ViewClickEvent) {
        if (evt.mapPoint) {
            const screenPoint = {
                x: evt.x,
                y: evt.y
            };
            const response = await this.mapView.hitTest(screenPoint);
            if (response.results.length) {
                const graphic = response.results[0].graphic;

                const matchingCallout = this.callouts.find(c => c.id === graphic.attributes.id);
                if (matchingCallout) {
                    if (this.activeCallout && matchingCallout.id !== this.activeCallout.id) {
                        this.teardownSketch();
                    }
                    this.activeCallout = matchingCallout;
                    //if the callout being selected has an image, update these properties
                    if (this.activeCallout.pictureMarkerSymbolProperties && this.activeCallout.pictureMarkerSymbolProperties.url) {
                        this.cbImageSource = this.activeCallout.pictureMarkerSymbolProperties.url;
                        this.imageOn = true;
                    }
                    this.doAddCallout = false;
                    this.setEditing();
                }
            } else {
                // Clicked somewhere on map that's not a graphic.
                if (this.doAddCallout) {
                    this.doAddCallout = false;
                    this.activeCallout = new Callout(evt.mapPoint);
                    this.callouts.push(this.activeCallout);
                    this.activeCallout.graphicsLayer = await this.getGraphicLayer(this.getGraphics(this.activeCallout));
                    this.cbImageSource = null;
                    this.imageOn = false;
                    await this.activeCallout.graphicsLayer.when();
                    this.setEditing();
                } else {
                    this.activeCallout = null;
                    this.doAddCallout = true;
                    this.teardownSketch();
                }
            }
            this.changeDetectorRef.detectChanges();
        }
    }

    private sketchUpdate(e: __esri.SketchViewModelUpdateEvent) {
        if (this.activeCallout && this.activeCallout.textGraphic && this.activeCallout.calloutGraphic) {
            this.activeCallout.textGraphic.geometry = (<__esri.Polygon>this.activeCallout.calloutGraphic.geometry).centroid.clone();
            const { type, ...symbolProperties } = this.activeCallout.symbolProperties;
            this.activeCallout.calloutGraphic.symbol = new SimpleFillSymbol(symbolProperties);
            this.activeCallout.graphicsLayer.graphics.reorder(this.activeCallout.textGraphic, 1);
        }

        /* TODO: Implement callout box pseudo-anchor point */
        if (e.toolEventInfo && (e.toolEventInfo.type === 'move-stop' || e.toolEventInfo.type === 'scale-stop')) {
            this.activeCallout.setGeometry(this.mapView.scale);
            if (e.toolEventInfo.type === 'scale-stop' && this.prevMapPointX) {
                this.activeCallout.mapPoint.x = this.prevMapPointX;
            }
            this.updateGraphic(this.activeCallout);
            this.changeDetectorRef.detectChanges();
        }
    }

    private async setEditing() {
        if (this.activeCallout) {
            this.initSketch();
            await this.resetSketch();
        } else {
            this.teardownSketch();
        }
    }

    private async resetSketch(fireRefreshLayers = true) {
        this.sketchViewModel.cancel();
        this.initializing = true;
        await this.sketchViewModel.update(this.activeCallout.calloutGraphic, { tool: 'transform' });
        (<any>this.sketchViewModel).activeComponent.layer.mapcoreProps = { hideInLegend: true };
        if (fireRefreshLayers && this.loaded) {
            this.layerService.refreshLayers();
        }
        this.loaded = true;
    }

    private getGraphics(callout: Callout) {
        callout.calloutGraphic = new Graphic({
            attributes: {
                type: 'callout',
                part: 'box',
                id: callout.id,
                boxAlignment: callout.boxAlignment,
                tailSize: callout.tailSize,
                boxWidth: callout.boxWidth,
                boxHeight: callout.boxHeight,
                cornerRadius: callout.cornerRadius
            },
            geometry: {
                type: 'polygon',
                rings: callout.getGeometry(this.mapView.scale),
                spatialReference: this.mapView.spatialReference
            },
            symbol: callout.symbolProperties
        } as __esri.GraphicProperties);

        if (this.imageOn) {
            callout.textGraphic = new Graphic({
                attributes: {
                    type: 'callout',
                    part: 'image',
                    id: callout.id
                },
                geometry: (<__esri.Polygon>callout.calloutGraphic.geometry).centroid.clone(),
                symbol: this.getPictureMarkerSymbolProperties(callout)
            } as __esri.GraphicProperties);
            if (!(callout.textGraphic.symbol as any).height) {
                (callout.textGraphic.symbol as any).height = 200;
            }
            if (!(callout.textGraphic.symbol as any).width) {
                (callout.textGraphic.symbol as any).width = 200;
            }
        } else {
            callout.textGraphic = new Graphic({
                attributes: {
                    type: 'callout',
                    part: 'text',
                    id: callout.id
                },
                geometry: (<__esri.Polygon>callout.calloutGraphic.geometry).centroid.clone(),
                symbol: this.getTextSymbolProperties(callout)
            } as __esri.GraphicProperties);
        }

        return [callout.calloutGraphic, callout.textGraphic];
    }

    private getTextSymbolProperties(callout: Callout) {
        const textOffset = callout.getTextOffset();
        return {
            type: 'text',
            xoffset: textOffset.x,
            yoffset: textOffset.y,
            lineWidth: (callout.boxWidth - (callout.horizontalPadding * widthPaddingMultiplier)) / pixelsInAPoint,
            ...callout.textProperties
        } as __esri.TextSymbolProperties;
    }
    private getPictureMarkerSymbolProperties(callout: Callout) {
        const textOffset = callout.getTextOffset();
        return {
            type: 'picture-marker',
            xoffset: textOffset.x,
            yoffset: textOffset.y,
            lineWidth: (callout.boxWidth - (callout.horizontalPadding * widthPaddingMultiplier)) / pixelsInAPoint,
            ...callout.pictureMarkerSymbolProperties
        } as __esri.PictureMarkerSymbolProperties;
    }

    private async getGraphicLayer(graphics: __esri.Graphic[]) {
        return await this.layerService.addGraphicsLayer({ title: `Callout ${this.callouts.length}`, graphics }, { identifyEnabled: false });
    }

    updateGraphic(callout: Callout) {
        if (!this.prevMapPointX) {
            this.prevMapPointX = (this.activeCallout.calloutGraphic.geometry as any).rings[0][0][0];
        }

        callout.normalizeMinMax();
        const updatedGraphics = this.getGraphics(callout);
        callout.graphicsLayer.graphics.removeAll();
        callout.graphicsLayer.graphics.addMany(updatedGraphics);
        if (this.sketchViewModel && this.sketchViewModel.state !== 'disabled') {
            this.resetSketch();
        }
        this.prevMapPointX = (updatedGraphics[0].geometry as any).rings[0][0][0];
        this.changeDetectorRef.detectChanges();
    }

    initializing = false;
    private readonly listners: IHandle[] = [];
    private setupHandleHiddenLayer() {
        this.initializing = true;
        const stopInitialCheck = util.debounce(() => { this.initializing = false; }, initilizeDebounceTime);
        this.listners.push(this.layerService.mapRef.allLayers.on('before-add', (event) => {
            if (this.initializing && event.item.listMode === 'hide') {
                event.item.set('hideInLegend', true);
                event.item.set('calloutToolGeneratedLayer', true);
                stopInitialCheck();
            }
        }));
        this.listners.push(this.layerService.mapRef.allLayers.on('after-remove', (event) => {
            if (event.item.get<boolean>('calloutToolGeneratedLayer')) {
                this.initializing = true;
            }
        }));
    }
    private teardownHandleHiddenLayer() {
        while (this.listners.length) {
            this.listners.pop().remove();
        }
    }

    onFileSelected(event) {
        //store image
        if (event.target.files.length === 0 || event.target.files.length > 1) {
            return;
        }
        this.selectedImage = <File>event.target.files[0];

        //read file and update img src to display image
        const fr: FileReader = new FileReader();
        fr.readAsDataURL(this.selectedImage);
        fr.onload = (_event) => {
            this.cbImageSource = fr.result;
            this.imageOn = true;

            this.activeCallout.pictureMarkerSymbolProperties.url = this.cbImageSource;
            if (this.activeCallout.boxHeight < 300) {
                this.activeCallout.boxHeight = 300;
                this.activeCallout.calloutGraphic.attributes.boxHeight = 300;
            }
            if (this.activeCallout.boxWidth < 300) {
                this.activeCallout.boxWidth = 300;
                this.activeCallout.calloutGraphic.attributes.boxWidth = 300;
            }

            this.updateGraphic(this.activeCallout);
        };
    }
    removeImage() {
        if (this.cbImageSource) {
            this.cbImageSource = null;
            this.imageOn = false;
            this.activeCallout.pictureMarkerSymbolProperties.url = null;
            this.updateGraphic(this.activeCallout);
        }
    }
}
